import type React from 'react'
import { useRef, useState } from 'react'

import { useValueSetter } from './useValueSetter'

type InputProps = React.InputHTMLAttributes<HTMLInputElement>

// React overrides the setter for `value` so we need to use
// `Object.getOwnPropertyDescriptor` to get the original setter
function setValue (input: HTMLInputElement, value: string) {
  const setValue = Object?.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set

  setValue?.call(input, value)
}

export function useClearableState (
  defaultValue: InputProps['defaultValue'],
  value: InputProps['value'],
  onChange: InputProps['onChange']
) {
  const isUncontrolled = value === undefined

  const [isFilled, setFilled] = useState(isUncontrolled ? !!defaultValue : null)
  const ref = useRef<HTMLInputElement>(null)

  // Tracks changes made to the input value directly via the DOM (for example,
  // by `react-hook-form`).
  useValueSetter(ref, (value) => {
    setFilled(!!value)
  })

  const clearValue = () => {
    if (ref.current) {
      setValue(ref.current, '')
      setFilled(false)
      ref.current.focus()
      ref.current.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUncontrolled) {
      setFilled(event.target.value !== '')
    }

    onChange?.(event)
  }

  const isClearable =
    (isUncontrolled && isFilled) || (!isUncontrolled && !!value && !!onChange)

  return { clearValue, handleChange, ref, isClearable }
}
